// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {boosterBadgeMonths, boosterBadgeTier, boosterBadgeUrl} from '@app/features/boost/utils/BoosterBadge';
import {mediaUrl} from '@app/features/messaging/utils/MessagingUrlUtils';
import {
	premiumBadgeUrl,
	premiumPackageBadgeUrl,
	premiumPackageMonths,
} from '@app/features/premium/utils/PremiumBadgeAsset';
import type {User} from '@app/features/user/models/User';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import type {InventoryBadge} from './BadgeInventoryState';
import {botDeveloperBadgeUrl, botDeveloperTier, staffBadgeUrl} from './BadgeTiers';

export const BUILTIN_BADGE_NAMES = {
	staff: msg({message: 'Staff', comment: 'Name of the staff badge in the owned badge inventory.'}),
	partner: msg({message: 'Partner', comment: 'Name of the partner badge in the owned badge inventory.'}),
	bug_hunter: msg({message: 'Bug hunter', comment: 'Name of the bug hunter badge in the owned badge inventory.'}),
	premium: msg({message: 'Plus', comment: 'Name of the active subscription crown in the owned badge inventory.'}),
	premium_package: msg({
		message: 'Early supporter',
		comment: 'Name of the permanent paid package badge in the owned badge inventory.',
	}),
	premium_sequence: msg({
		message: 'Visionary ID',
		comment: 'Name of the numbered lifetime subscription badge in the owned badge inventory.',
	}),
	booster: msg({
		message: 'Server booster',
		comment: 'Name of the server booster badge in the owned badge inventory.',
	}),
	bot_developer: msg({
		message: 'Bot developer',
		comment: 'Name of the bot developer badge in the owned badge inventory, given for verified bots.',
	}),
};
const descriptions = {
	staff: msg({message: 'A member of the team.', comment: 'Short description of an owned staff badge.'}),
	partner: msg({message: 'A community partner.', comment: 'Short description of an owned partner badge.'}),
	bug_hunter: msg({message: 'Helps find and report bugs.', comment: 'Short description of an owned bug hunter badge.'}),
	premium: msg({message: 'Your active Plus subscription.', comment: 'Short description of the subscription badge.'}),
	premium_package: msg({
		message: 'Your largest paid Plus package.',
		comment: 'Short description of the permanent package badge.',
	}),
	premium_sequence: msg({
		message: 'Your lifetime subscription number.',
		comment: 'Short description of the Visionary ID badge.',
	}),
	booster: msg({
		message: 'Grows while you boost a community without a break.',
		comment: 'Short description of the server booster badge, whose art changes with the months of unbroken boosting.',
	}),
	bot_developer: msg({
		message: 'Grows as your verified bots join more servers.',
		comment: 'Short description of the bot developer badge, whose tier climbs with the servers of the verified bots.',
	}),
};
const assets = {staff: 'staff-v2.webp', partner: 'partner.svg', bug_hunter: 'bug-hunter-v2.webp'};

export function inventoryBadgePresentation(badge: InventoryBadge, user: User, i18n: I18n) {
	if (badge.source === 'catalog') {
		return {
			name: badge.description,
			description: badge.description,
			iconUrl: mediaUrl(`branding/badge-${badge.badge_id}/${badge.icon}.webp?size=128`),
		};
	}
	const name = i18n._(BUILTIN_BADGE_NAMES[badge.key]);
	const description = i18n._(descriptions[badge.key]);
	if (badge.source === 'boost') {
		return {
			name,
			description,
			iconUrl: boosterBadgeUrl(boosterBadgeTier(boosterBadgeMonths(new Date(badge.since), new Date()))),
		};
	}
	if (badge.source === 'bot_developer') {
		return {name, description, iconUrl: botDeveloperBadgeUrl(botDeveloperTier(badge.guilds))};
	}
	if (badge.source === 'flag' && badge.key === 'staff') {
		return {name, description, iconUrl: staffBadgeUrl(badge.tier ?? null)};
	}
	if (badge.key === 'premium_sequence') {
		return {name, description, text: user.premiumLifetimeSequence != null ? `#${user.premiumLifetimeSequence}` : '#'};
	}
	const packageMonths = (badge.source === 'premium' ? badge.months : undefined) ?? premiumPackageMonths(user.flags);
	const iconUrl =
		badge.key === 'premium'
			? premiumBadgeUrl()
			: badge.key === 'premium_package'
				? packageMonths != null
					? premiumPackageBadgeUrl(packageMonths)
					: undefined
				: `${RuntimeConfig.staticCdnEndpoint}/badges/micup-${assets[badge.key]}?v=3`;
	// Without the server's tier, hidden package flags do not identify one; never guess a package the user owns.
	return {name, description, iconUrl, text: iconUrl ? undefined : '∞'};
}
